import { useState, useCallback, useEffect } from "react";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useOnboardingBlocked } from "@/pages/onboarding/OnboardingStep";
import toast from "react-hot-toast";

import { OnboardingAfterTrail } from "@/components/onboarding/OnboardingAfterTrail";
import { OnboardingBrandColumn } from "@/components/onboarding/OnboardingBrandColumn";
import { OnboardingBrandMark } from "@/components/onboarding/OnboardingBrandMark";
import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { OnboardingStepTag } from "@/components/onboarding/OnboardingStepTag";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn, toTitleCase } from "@/lib/utils";
import { updateLegalData, getDgiPersonName } from "@/services/authService";
import type { CompanyTypeValue } from "@/services/authService";

const CI_MIN_DIGITS = 7;
const CI_MAX_DIGITS = 8;

function normalizeCi(value: string): string {
  return value.replace(/\D/g, "").trim();
}

function ciLengthValid(value: string): boolean {
  const n = normalizeCi(value).length;
  return n >= CI_MIN_DIGITS && n <= CI_MAX_DIGITS;
}

export interface LegalDataStepProps {
  companyType?: CompanyTypeValue | null;
  /** Company legal name for the declaration checkbox. When set, checkbox is shown and required. */
  legalName?: string | null;
  initialValues?: {
    firstName: string | null;
    secondName: string | null;
    firstLastName: string | null;
    secondLastName: string | null;
    documentType: "national_id" | "passport" | null;
    documentNumber: string | null;
  };
  onSuccess: () => void;
}

export function LegalDataStep({
  companyType = null,
  legalName = null,
  initialValues,
  onSuccess,
}: LegalDataStepProps): JSX.Element {
  const { t } = useTranslation();
  const reportBlocked = useOnboardingBlocked();
  const [legalDeclarationChecked, setLegalDeclarationChecked] = useState(false);
  const showDocumentTypeSelector =
    companyType !== "monotributo" && companyType !== "unipersonal";

  const [documentType, setDocumentType] = useState<
    "national_id" | "passport" | ""
  >(initialValues?.documentType ?? "national_id");
  const [documentNumber, setDocumentNumber] = useState(
    initialValues?.documentNumber ?? "",
  );
  const [dgiValidated, setDgiValidated] = useState(false);
  const [validating, setValidating] = useState(false);

  const [firstName, setFirstName] = useState(initialValues?.firstName ?? "");
  const [secondName, setSecondName] = useState(
    initialValues?.secondName ?? "",
  );
  const [firstLastName, setFirstLastName] = useState(
    initialValues?.firstLastName ?? "",
  );
  const [secondLastName, setSecondLastName] = useState(
    initialValues?.secondLastName ?? "",
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!showDocumentTypeSelector) setDocumentType("national_id");
  }, [showDocumentTypeSelector]);

  // If we land already with names + CI, treat as already-validated.
  const hasInitialNames =
    (initialValues?.firstName?.trim()?.length ?? 0) > 0 ||
    (initialValues?.firstLastName?.trim()?.length ?? 0) > 0;
  useEffect(() => {
    if (
      initialValues?.documentType === "national_id" &&
      hasInitialNames &&
      (initialValues?.documentNumber?.trim()?.length ?? 0) > 0
    ) {
      setDgiValidated(true);
    }
  }, []); // mount-only

  const effectiveDocType =
    documentType || (showDocumentTypeSelector ? "" : "national_id");
  const isNationalId = effectiveDocType === "national_id";
  const isPassport = effectiveDocType === "passport";

  const formUnlocked =
    (isNationalId && dgiValidated) ||
    (isPassport && documentNumber.trim().length > 0);

  const canValidateNationalId =
    isNationalId &&
    ciLengthValid(documentNumber) &&
    !dgiValidated &&
    !validating;

  const handleValidateDocument = useCallback(async () => {
    if (!canValidateNationalId) return;
    const ci = normalizeCi(documentNumber);
    setValidating(true);
    setDgiValidated(false);
    try {
      const data = await getDgiPersonName(ci);
      const hasName =
        (data.primerNombre?.trim()?.length ?? 0) > 0 ||
        (data.primerApellido?.trim()?.length ?? 0) > 0;
      if (!hasName) {
        reportBlocked("legal_data_dgi_no_name");
        toast.error(t("onboarding.legalDataNoDataFound"));
        return;
      }
      if (data.primerNombre) setFirstName(toTitleCase(data.primerNombre));
      if (data.segundoNombre) setSecondName(toTitleCase(data.segundoNombre));
      else setSecondName("");
      if (data.primerApellido)
        setFirstLastName(toTitleCase(data.primerApellido));
      if (data.segundoApellido)
        setSecondLastName(toTitleCase(data.segundoApellido));
      else setSecondLastName("");
      setDgiValidated(true);
      toast.success(t("onboarding.legalDataDocumentValidated"));
    } catch {
      reportBlocked("legal_data_document_lookup_failed");
      toast.error(t("onboarding.legalDataNoDataFound"));
    } finally {
      setValidating(false);
    }
  }, [canValidateNationalId, documentNumber, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const first = firstName.trim();
    const firstLast = firstLastName.trim();
    const docType = effectiveDocType as "national_id" | "passport";
    const docNum = documentNumber.trim();
    if (!first || !firstLast || !docType || !docNum) return;
    setSubmitting(true);
    try {
      await updateLegalData({
        firstName: first,
        secondName: secondName.trim() || null,
        firstLastName: firstLast,
        secondLastName: secondLastName.trim() || null,
        documentType: docType,
        documentNumber: docNum,
      });
      onSuccess();
    } catch {
      reportBlocked("legal_data_save_failed");
      toast.error(t("onboarding.legalDataError"));
    } finally {
      setSubmitting(false);
    }
  };

  const nameFormValid =
    firstName.trim() &&
    firstLastName.trim() &&
    (isNationalId || isPassport) &&
    documentNumber.trim();
  const declarationRequired = (legalName ?? "").trim().length > 0;
  const canContinue =
    formUnlocked &&
    Boolean(nameFormValid) &&
    (!declarationRequired || legalDeclarationChecked) &&
    !submitting;

  return (
    <OnboardingShell
      form={
        <form
          onSubmit={handleSubmit}
          style={{ display: "contents" }}
        >
          <OnboardingBrandMark />
          <OnboardingStepTag
            step={3}
            label={t("onboarding.step3.tagLabel", "Tu identidad")}
          />
          <h1 className="fx-onb-h1">
            {t("onboarding.step3.titleLead", "Verificá tu")}{" "}
            <span className="ital">
              {t("onboarding.step3.titleAccent", "identidad.")}
            </span>
          </h1>
          <p className="fx-onb-lead">
            {t(
              "onboarding.step3.lead",
              "Completá los datos tal como figuran en tu documento. Fiscalo los verifica contra el padrón nacional.",
            )}
          </p>

          <div className="fx-onb-form">
            <div className="fx-onb-form-row with-btn">
              {showDocumentTypeSelector ? (
                <div className="fx-onb-field">
                  <label htmlFor="legal-documentType">
                    {t("onboarding.legalDataDocumentType", "Tipo de documento")}
                  </label>
                  <Select
                    value={documentType || undefined}
                    onValueChange={(v) => {
                      setDocumentType(v as "national_id" | "passport");
                      setDgiValidated(false);
                    }}
                    disabled={submitting}
                  >
                    <SelectTrigger
                      id="legal-documentType"
                      className="h-auto rounded-[10px] border-[var(--fx-ink-200)] bg-white px-4 py-[13px] text-[15px] text-[var(--fx-ink-900)] focus:ring-[var(--fx-accent-500)] focus:border-[var(--fx-accent-500)]"
                    >
                      <SelectValue
                        placeholder={t(
                          "onboarding.legalDataDocumentTypePlaceholder",
                          "Seleccioná",
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="national_id">
                        {t(
                          "onboarding.legalDataDocumentTypeNationalId",
                          "Cédula de identidad",
                        )}
                      </SelectItem>
                      <SelectItem value="passport">
                        {t(
                          "onboarding.legalDataDocumentTypePassport",
                          "Pasaporte",
                        )}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="fx-onb-field">
                  <label>
                    {t(
                      "onboarding.legalDataDocumentType",
                      "Tipo de documento",
                    )}
                  </label>
                  <input
                    type="text"
                    value={t(
                      "onboarding.legalDataDocumentTypeNationalId",
                      "Cédula de identidad",
                    )}
                    readOnly
                  />
                </div>
              )}
              <div className="fx-onb-field">
                <label htmlFor="legal-documentNumber">
                  {t(
                    "onboarding.legalDataDocumentNumber",
                    "Número de documento",
                  )}
                </label>
                <input
                  id="legal-documentNumber"
                  value={documentNumber}
                  onChange={(e) => {
                    setDocumentNumber(e.target.value);
                    if (isNationalId) setDgiValidated(false);
                  }}
                  placeholder={t(
                    "onboarding.legalDataDocumentNumberPlaceholder",
                    "Ej. 4 619 906 9",
                  )}
                  maxLength={64}
                  disabled={submitting || dgiValidated}
                  readOnly={dgiValidated}
                />
              </div>
              <button
                type="button"
                className={cn(
                  "fx-onb-btn-validate",
                  dgiValidated && "success",
                )}
                onClick={
                  isNationalId
                    ? handleValidateDocument
                    : () => {
                        /* Passport unlocks via doc number; nothing to call */
                      }
                }
                disabled={
                  isNationalId
                    ? !canValidateNationalId && !dgiValidated
                    : !documentNumber.trim()
                }
              >
                {validating ? (
                  <>
                    <Loader2 className="animate-spin" aria-hidden />
                    {t("onboarding.step3.validating", "Validando…")}
                  </>
                ) : dgiValidated || (isPassport && documentNumber.trim()) ? (
                  <>
                    <Check aria-hidden />
                    {t("onboarding.step3.validatedButton", "Validado")}
                  </>
                ) : (
                  <>{t("onboarding.step3.validateButton", "Validar")}</>
                )}
              </button>
            </div>

            {formUnlocked ? (
              <div className="fx-onb-verified-block">
                <div className="vb-head">
                  <span className="title">
                    {t("onboarding.step3.identityTitleLead", "Tu identidad")}{" "}
                    <span className="ital">
                      {t("onboarding.step3.identityTitleAccent", "confirmada.")}
                    </span>
                  </span>
                  {isNationalId && dgiValidated ? (
                    <span className="verified">
                      <span className="dot">
                        <Check aria-hidden />
                      </span>
                      {t("onboarding.step3.verifiedPill", "Padrón nacional")}
                    </span>
                  ) : null}
                </div>
                <div className="vb-grid">
                  <div className="fx-onb-vb-item">
                    <div className="k">
                      {t("onboarding.legalDataFirstName", "Primer nombre")}
                    </div>
                    <div className="v">
                      <input
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        disabled={submitting}
                        style={{
                          background: "transparent",
                          border: 0,
                          padding: 0,
                          fontSize: "14.5px",
                          fontWeight: 500,
                          color: "var(--fx-s-text)",
                          width: "100%",
                        }}
                      />
                    </div>
                  </div>
                  <div className="fx-onb-vb-item">
                    <div className="k">
                      {t("onboarding.legalDataSecondName", "Segundo nombre")}
                    </div>
                    <div className="v">
                      <input
                        value={secondName}
                        onChange={(e) => setSecondName(e.target.value)}
                        disabled={submitting}
                        placeholder={t(
                          "onboarding.step3.optional",
                          "(opcional)",
                        )}
                        style={{
                          background: "transparent",
                          border: 0,
                          padding: 0,
                          fontSize: "14.5px",
                          fontWeight: 500,
                          color: "var(--fx-s-text)",
                          width: "100%",
                        }}
                      />
                    </div>
                  </div>
                  <div className="fx-onb-vb-item">
                    <div className="k">
                      {t(
                        "onboarding.legalDataFirstLastName",
                        "Primer apellido",
                      )}
                    </div>
                    <div className="v">
                      <input
                        value={firstLastName}
                        onChange={(e) => setFirstLastName(e.target.value)}
                        disabled={submitting}
                        style={{
                          background: "transparent",
                          border: 0,
                          padding: 0,
                          fontSize: "14.5px",
                          fontWeight: 500,
                          color: "var(--fx-s-text)",
                          width: "100%",
                        }}
                      />
                    </div>
                  </div>
                  <div className="fx-onb-vb-item">
                    <div className="k">
                      {t(
                        "onboarding.legalDataSecondLastName",
                        "Segundo apellido",
                      )}
                    </div>
                    <div className="v">
                      <input
                        value={secondLastName}
                        onChange={(e) => setSecondLastName(e.target.value)}
                        disabled={submitting}
                        placeholder={t(
                          "onboarding.step3.optional",
                          "(opcional)",
                        )}
                        style={{
                          background: "transparent",
                          border: 0,
                          padding: 0,
                          fontSize: "14.5px",
                          fontWeight: 500,
                          color: "var(--fx-s-text)",
                          width: "100%",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {declarationRequired && formUnlocked ? (
              <button
                type="button"
                className={cn(
                  "fx-onb-declare",
                  legalDeclarationChecked && "checked",
                )}
                onClick={() => setLegalDeclarationChecked((v) => !v)}
                disabled={submitting}
                aria-pressed={legalDeclarationChecked}
              >
                <span className="ckbox">
                  <Check aria-hidden />
                </span>
                <span className="body">
                  <span className="lbl">
                    {t(
                      "onboarding.legalDataDeclarationLabel",
                      "Declaración legal",
                    )}
                  </span>
                  <span className="txt" data-mag-private>
                    {t("onboarding.legalDataDeclarationCheckbox", {
                      legalName:
                        legalName?.trim() ||
                        t("onboarding.introHeadlineFallbackCompany"),
                      defaultValue:
                        "Declaro que soy titular o representante legal de {{legalName}}. Entiendo que esta declaración tiene carácter legal.",
                    })}
                  </span>
                </span>
              </button>
            ) : null}
          </div>

          <div className="fx-onb-actions">
            <span className="side" />
            <span className="side">
              <button
                type="submit"
                className="fx-onb-btn fx-onb-btn-primary"
                disabled={!canContinue}
              >
                {submitting ? (
                  <Loader2 className="animate-spin" aria-hidden />
                ) : (
                  <>
                    {t("onboarding.continue", "Continuar")}
                    <ArrowRight aria-hidden />
                  </>
                )}
              </button>
            </span>
          </div>
        </form>
      }
      brand={
        <OnboardingBrandColumn
          eyebrow={t(
            "onboarding.step3.brandEyebrow",
            "Por qué te pedimos esto",
          )}
          quote={
            <>
              {t(
                "onboarding.step3.brandQuoteLead",
                "Verificamos tu identidad porque tu CFE depende de quién lo emite.",
              )}{" "}
              <span className="accent">
                {t("onboarding.step3.brandQuoteAccent", "Cumplimos por vos.")}
              </span>
            </>
          }
          currentStep={3}
          afterTrail={<OnboardingAfterTrail />}
          valueBlock={{
            head: t(
              "onboarding.step3.brandValueHead",
              "Tu información está protegida",
            ),
            items: [
              t(
                "onboarding.step3.brandValue1",
                "Cifrado extremo a extremo (TLS 1.3)",
              ),
              t(
                "onboarding.step3.brandValue2",
                "Verificación contra el padrón nacional",
              ),
              t(
                "onboarding.step3.brandValue3",
                "No compartimos tus datos con terceros",
              ),
            ],
          }}
        />
      }
    />
  );
}
